import type {ReactElement} from 'react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AiRedesignPromptBox} from '@Components/ai-resdesign-prompt-box';
import styles from './ai-chat-window.module.scss';
import type {EditableComponentsMap, PromptAttachment, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {CircularProgressLoader, LOADER_COLOR} from '@Components/circular-progress-loader';
import '@Global/create-from-scratch';
import type {AiChatWindowProps, MessagesResponse} from './ai-chat-window.types';
import {MessageRole} from './ai-chat-window.types';
import {useChatStream} from '@Components/ai-chat-window/hooks/useChatStream';
import {ChatMessages} from '@Components/ai-chat-window/components/chat-messages-display/chat-messages';
import {ChatControls} from '@Components/ai-chat-window/components/chat-controls/chat-controls';
import {createUserRequestParts, findLastUserMessage, getAiChatCompletedStateForInProgressChat, scrollToBottom} from '@Components/ai-chat-window/lib/ai-chat-messages.library';

export function AiChatWindow({chatId, onChatStart, ...props}: AiChatWindowProps): ReactElement {
  const [chatLoading, setChatLoading] = useState(false);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);

  const [waitingForRefreshedChatToFinish, setWaitingForRefreshedChatToFinish] = useState(false);

  const {messages, setMessages, isStreaming, setIsStreaming, tempUserMessageRef, startGenerationStream, stopGeneration, disconnect, messageSessionUidRef} = useChatStream({
    chatId,
    onChatStart,
    refreshChatHistory: props.refreshChatHistory,
    setChatId: props.setChatId,
  });

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      disconnect();
      return;
    }

    void getChatHistory(chatId);

    return (): void => {
      disconnect();
    };
  }, [chatId, setMessages, disconnect]);

  useEffect(() => {
    if (props.chatOnLoadParams?.userRequestParts) {
      const userRequestParts = props.chatOnLoadParams.userRequestParts;
      tempUserMessageRef.current = {role: MessageRole.USER, parts: userRequestParts};
      void startGenerationStream(userRequestParts);
    }
  }, []);

  const handleRefreshedInProgressChat = async (activeMessageSessionUid?: string): Promise<void> => {
    setWaitingForRefreshedChatToFinish(!!activeMessageSessionUid);

    if (activeMessageSessionUid) {
      setIsStreaming(true);
      setWaitingForRefreshedChatToFinish(true);

      if (chatId) {
        const chatCompletedStateForInProgressChatResponse = await getAiChatCompletedStateForInProgressChat(chatId, activeMessageSessionUid);

        if (chatCompletedStateForInProgressChatResponse) {
          setMessages(chatCompletedStateForInProgressChatResponse.history);
        }
      }
    }
  };

  const getChatHistory = useCallback(
    async (chatId: string): Promise<void> => {
      try {
        setChatLoading(true);
        const response = (await window.PMW.readLocal('ai/messages', {chatId})) as MessagesResponse;
        setMessages(response.history);
        setChatLoading(false);

        if (response.activeMessageSessionUid) {
          messageSessionUidRef.current = response.activeMessageSessionUid;
        }

        await handleRefreshedInProgressChat(response.activeMessageSessionUid);
      } catch (e) {
        console.error(e);
      } finally {
        setChatLoading(false);
        setWaitingForRefreshedChatToFinish(false);
        setIsStreaming(false);
      }
    },
    [handleRefreshedInProgressChat, messageSessionUidRef, setChatLoading, setMessages, setIsStreaming]
  );

  const onMessageRefMount = useCallback((container: HTMLDivElement): void => {
    if (!container) {
      return;
    }
    messagesRef.current = container;
  }, []);

  useEffect(() => {
    scrollToBottom(messagesRef.current);
  }, [messages, messagesRef.current]);

  const handleRetry = useCallback(async (): Promise<void> => {
    const lastUserMessage = findLastUserMessage(messages);

    if (!lastUserMessage?.parts) {
      console.error('Retry failed: Could not find the last user message to retry.');
      return;
    }

    if (!tempUserMessageRef.current) {
      setMessages((prev) => [...prev, lastUserMessage]);
    }
    await startGenerationStream(lastUserMessage.parts);
  }, [messages, startGenerationStream, setMessages]);

  const handleRegenerate = (): void => {
    void generate('Regenerate this Design'); // zain todo: (wip) discuss the prompt for this with maryam, is experimental for now
  };

  const generate = useCallback(
    async (prompt: string, _options?: SelectedPromptOptionValues, _editableComponentsMap?: EditableComponentsMap, promptAttachments?: PromptAttachment[]): Promise<void> => {
      const userRequestParts = createUserRequestParts(prompt, promptAttachments);
      tempUserMessageRef.current = {role: MessageRole.USER, parts: userRequestParts};
      await startGenerationStream(userRequestParts);
    },
    [startGenerationStream, tempUserMessageRef]
  );

  const abortMessage = useCallback(async (): Promise<void> => {
    await stopGeneration();
    setWaitingForRefreshedChatToFinish(false);
  }, [stopGeneration]);

  const renderPromptBox = (): ReactElement => {
    return (
      <div className={`flex-v-row`}>
        <ChatControls chatId={chatId} isStreaming={isStreaming} onStop={abortMessage} />

        <AiRedesignPromptBox
          regenerateContent={generate}
          placeholder={window.i18next.t('pmwjs_create_a_design_template_elipsis')}
          editableComponentsMap={{
            [EditableComponent.IMAGE]: true,
          }}
          promptOptionsForEditableComponents={{
            [EditableComponent.IMAGE]: [],
          }}
          unselectOnOutsideClick
          isPromptBoxSelectedOnRender
          allowPromptAttachments
          promptBoxClasses={styles.promptBox}
        />
      </div>
    );
  };

  const renderMessages = (): ReactElement => {
    if (chatLoading) {
      return (
        <div className={`_full-width flex-center ${styles.loaderContainer}`}>
          <CircularProgressLoader color={LOADER_COLOR.AI_DARK} classes={'spacing-m-l-0'} />
        </div>
      );
    }

    return (
      <ChatMessages
        waitingForRefreshedChatToFinish={waitingForRefreshedChatToFinish}
        messages={messages}
        chatId={chatId}
        bubbleClassNames={styles.bubble}
        onRetry={handleRetry}
        onMessageRefMount={onMessageRefMount}
        onRegenerate={handleRegenerate}
      />
    );
  };

  return (
    <div ref={shellRef} className={styles.shell}>
      <div className={`${styles.stage} ${styles.withMessages}`}>
        {renderMessages()}
        {renderPromptBox()}
      </div>
    </div>
  );
}
